import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import AdminShell from "./AdminShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Global inbox — every homeowner enquiry across every submission in one place.
 * Two tabs, unread badges per project, inline reply on open tickets.
 * Poll every 30s so replies from teammates appear without a manual refresh.
 */
export default function AdminInbox() {
  const [queries, setQueries] = useState([]);
  const [tab, setTab] = useState("open");
  const [expanded, setExpanded] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = React.useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/queries`, { withCredentials: true });
      setQueries(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load inbox.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load]);

  const send = async (id) => {
    const reply = (drafts[id] || "").trim();
    if (!reply) return;
    setBusyId(id);
    try {
      await axios.post(`${API}/admin/queries/${id}/reply`, { reply }, { withCredentials: true });
      setDrafts((d) => ({ ...d, [id]: "" }));
      setExpanded(null);
      toast.success("Reply sent.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send reply.");
    } finally { setBusyId(null); }
  };

  const filtered = useMemo(() => queries.filter((q) => q.status === tab), [queries, tab]);
  const openCount = queries.filter((q) => q.status === "open").length;
  const repliedCount = queries.length - openCount;

  // Group by submission so admins see who's asking what
  const grouped = useMemo(() => {
    const map = new Map();
    for (const q of filtered) {
      const arr = map.get(q.submission_id) || [];
      arr.push(q);
      map.set(q.submission_id, arr);
    }
    return Array.from(map.entries());
  }, [filtered]);

  const right = (
    <div style={{ display: "flex", gap: 6 }}>
      {[
        { key: "open", label: `Awaiting reply (${openCount})` },
        { key: "replied", label: `Replied (${repliedCount})` },
      ].map((t) => (
        <button
          key={t.key}
          type="button"
          data-testid={`inbox-tab-${t.key}`}
          onClick={() => { setTab(t.key); setExpanded(null); }}
          className="folk-pill"
          style={{
            padding: "8px 14px", fontSize: 12.5,
            background: tab === t.key ? "#68280E" : "transparent",
            color: tab === t.key ? "#FFF" : "#2A2828",
            border: `1.5px solid ${tab === t.key ? "#68280E" : "var(--folk-border)"}`,
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );

  return (
    <AdminShell eyebrow="Admin" title="Inbox" right={right}>
      {loading ? (
        <div style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 18, padding: 24, color: "var(--folk-muted)" }}>
          Loading messages…
        </div>
      ) : grouped.length === 0 ? (
        <div data-testid="inbox-empty" style={{ background: "var(--folk-card)", border: "1.5px dashed var(--folk-border)", borderRadius: 18, padding: 28, color: "var(--folk-muted)", textAlign: "center" }}>
          {tab === "open" ? "You're all caught up. No messages awaiting a reply." : "No replied messages yet."}
        </div>
      ) : (
        <div data-testid="inbox-list" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {grouped.map(([submissionId, items]) => {
            const first = items[0];
            const projectLabel = first.contact_name || first.contact_email || submissionId.slice(0, 8);
            return (
              <div key={submissionId} data-testid={`inbox-project-${submissionId}`} style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 18, padding: 18 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-hint)", fontWeight: 700 }}>Project</div>
                    <div style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "-0.028em", fontSize: 22, textTransform: "lowercase", marginTop: 2 }}>
                      {String(projectLabel).toLowerCase()}
                    </div>
                    {first.contact_email && (
                      <div style={{ fontSize: 12.5, color: "var(--folk-muted)", fontFamily: "Host Grotesk, sans-serif", marginTop: 2 }}>{first.contact_email}</div>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {tab === "open" && (
                      <span data-testid={`inbox-badge-${submissionId}`} style={{ background: "#68280E", color: "#FFF", borderRadius: 999, padding: "3px 10px", fontSize: 11.5, fontWeight: 700, fontFamily: "Host Grotesk, sans-serif" }}>
                        {items.length} awaiting
                      </span>
                    )}
                    <Link
                      to={`/admin/submissions/${submissionId}`}
                      data-testid={`inbox-open-workflow-${submissionId}`}
                      className="folk-pill"
                      style={{ textDecoration: "none", padding: "6px 12px", fontSize: 12, background: "transparent", color: "#68280E", border: "1.5px solid #68280E" }}
                    >
                      Open workflow →
                    </Link>
                  </div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {items.map((q) => {
                    const isOpen = expanded === q.id;
                    const canReply = q.status === "open";
                    return (
                      <div key={q.id} data-testid={`inbox-ticket-${q.id}`} style={{ background: "var(--folk-bg)", border: "1.5px solid var(--folk-border)", borderRadius: 12, overflow: "hidden" }}>
                        <button
                          type="button"
                          onClick={() => setExpanded(isOpen ? null : q.id)}
                          data-testid={`inbox-ticket-toggle-${q.id}`}
                          style={{ width: "100%", textAlign: "left", background: "transparent", border: "none", padding: "12px 14px", cursor: "pointer", display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline" }}
                        >
                          <span style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 13.5, color: "#2A2828", fontWeight: 600 }}>{q.subject || "(no subject)"}</span>
                          <span style={{ fontSize: 11.5, color: "var(--folk-muted)" }}>{new Date(q.created_at).toLocaleString()}</span>
                        </button>
                        {isOpen && (
                          <div style={{ padding: "0 14px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
                            <div style={{ background: "#FFF", border: "1px solid var(--folk-border)", borderRadius: 10, padding: 12, fontSize: 13, lineHeight: 1.55, whiteSpace: "pre-wrap", color: "#2A2828", fontFamily: "Host Grotesk, sans-serif" }}>
                              {q.message}
                            </div>
                            {q.admin_reply && (
                              <div style={{ background: "#FFFBC9", border: "1.5px solid rgba(104, 40, 14, 0.25)", borderRadius: 10, padding: 12 }}>
                                <div style={{ fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", color: "#68280E", fontWeight: 700, marginBottom: 4, fontFamily: "Host Grotesk, sans-serif" }}>
                                  Folk reply · {q.replied_at ? new Date(q.replied_at).toLocaleString() : ""}
                                </div>
                                <div style={{ fontSize: 13, color: "#1B1A1A", whiteSpace: "pre-wrap", lineHeight: 1.5, fontFamily: "Host Grotesk, sans-serif" }}>{q.admin_reply}</div>
                              </div>
                            )}
                            {canReply && (
                              <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                                <textarea
                                  data-testid={`inbox-reply-${q.id}`}
                                  className="folk-input"
                                  rows={3}
                                  value={drafts[q.id] || ""}
                                  onChange={(e) => setDrafts((d) => ({ ...d, [q.id]: e.target.value }))}
                                  placeholder="Reply as the Folk team — this emails them via Resend."
                                  style={{ flex: 1, resize: "vertical", fontSize: 13.5 }}
                                />
                                <button
                                  type="button"
                                  data-testid={`inbox-send-${q.id}`}
                                  onClick={() => send(q.id)}
                                  disabled={busyId === q.id || !(drafts[q.id] || "").trim()}
                                  className="folk-pill"
                                  style={{ padding: "8px 14px", fontSize: 12.5, background: "#68280E", color: "#FFF", border: "1.5px solid #68280E", opacity: (busyId === q.id || !(drafts[q.id] || "").trim()) ? 0.55 : 1 }}
                                >
                                  {busyId === q.id ? "Sending…" : "Send reply"}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AdminShell>
  );
}
